import {
  AdminUserAtMikatanoId,
  CompanyQuery,
} from "@/generated/graphql_billing";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import {
  Button,
  Cell,
  Font,
  Icon,
  List,
  Modal,
  TextLink,
} from "@moneyforward/mfx_biz_react_components";
import { createRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FormErrorMessage, FormSelectBox } from "@/components/molecules";

type Props = {
  company: CompanyQuery["company"];
  userSub: string;
  handleDeleteAdmin: (userSub: string) => Promise<void>;
  handleAddAdmin: (userSub: string) => Promise<void>;
  adminUsersAtMikatanoID: AdminUserAtMikatanoId[];
};

export const ContractAdmin = (props: Props) => {
  const {
    company,
    userSub,
    handleDeleteAdmin,
    handleAddAdmin,
    adminUsersAtMikatanoID,
  } = props;
  const admins = company.admins;
  const me = admins.find((admin) => admin.userSub === userSub);

  // 契約管理者の追加
  const addMethods = useForm({
    defaultValues: {
      admin: "",
    },
  });
  const addOptions = adminUsersAtMikatanoID
    .filter(
      (adminUser) =>
        !company.admins.find((admin) => admin.userSub === adminUser.sub)
    )
    .map((admin) => ({
      label: `${admin.lastName} ${admin.firstName}`,
      value: admin.sub,
    }));
  const [addedAdmin, setAddedAdmin] = useState<string>();
  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const addAdmin = async () => {
    await handleAddAdmin(addedAdmin);
  };

  const addAdminFormRef = createRef<HTMLFormElement>();

  const onAddAdminButtonClick = () => {
    if (!addAdminFormRef.current) return;

    addAdminFormRef.current.dispatchEvent(
      new Event("submit", { bubbles: true, cancelable: true })
    );
  };

  const addModalBody = (
    <FormProvider {...addMethods}>
      <form onSubmit={addMethods.handleSubmit(addAdmin)} ref={addAdminFormRef}>
        <div>
          <div css={modalBody}>
            <Font>契約管理者に追加する利用者を選択してください。</Font>
            <Font css={{ color: color.gray600 }}>
              ※Mikatano IDの「管理者」権限が必要です。利用者が表示されない場合は
              <TextLink href="/manage-users">利用者管理画面</TextLink>
              から利用者の権限をご確認ください。
            </Font>
          </div>
          <div css={modalSelectBox}>
            <FormSelectBox
              name="admin"
              options={addOptions}
              onChange={() => {
                setAddedAdmin(addMethods.getValues().admin);
              }}
              registerOptions={{
                required: "選択してください",
              }}
              placeholder="選択してください"
            />
            <FormErrorMessage name="admin" />
          </div>
        </div>
      </form>
    </FormProvider>
  );
  const addModalFooterButtons = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setIsAddModal(false)}
      >
        キャンセル
      </Button>
      <Button type="button" variant="primary" onClick={onAddAdminButtonClick}>
        追加
      </Button>
    </>
  );

  // 契約管理者の削除
  const [deletingName, setDeletingName] = useState<{
    name: string;
    userSub: string;
  }>();
  const deleteAdmin = async () => {
    await handleDeleteAdmin(deletingName.userSub);
  };
  const deleteModalBody = (
    <div css={modalBody}>
      <Font>{deletingName?.name}さんを契約管理者のリストから削除します。</Font>
      <Font>削除後にもう一度、契約管理者に追加することができます。</Font>
    </div>
  );
  const deleteModalFooterButtons = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setDeletingName(undefined)}
      >
        キャンセル
      </Button>
      <Button type="button" variant="destructive" onClick={deleteAdmin}>
        削除
      </Button>
    </>
  );

  return (
    <>
      {deletingName && (
        <div css={overlay}>
          <Modal
            title="契約管理者の削除"
            body={deleteModalBody}
            zIndex={20}
            footerButtons={deleteModalFooterButtons}
            onClose={() => setDeletingName(undefined)}
          />
        </div>
      )}
      {isAddModal && (
        <div css={overlay}>
          <Modal
            title="契約管理者の追加"
            body={addModalBody}
            zIndex={20}
            footerButtons={addModalFooterButtons}
            onClose={() => setIsAddModal(false)}
          />
        </div>
      )}
      <div css={container}>
        <div css={box}>
          <Font>
            契約管理者はプランやオプションの変更と追加、解約ができます。契約の管理を複数の利用者でおこなう場合、契約管理者を追加してください。
            <br />
            ※Mikatano IDの「管理者」権限を持つ利用者を追加することができます。
          </Font>
        </div>
        <div css={buttonContainer}>
          <Button variant="secondary" onClick={() => setIsAddModal(true)}>
            追加
          </Button>
        </div>
        <div>
          <List isHeader gridTemplateColumns="90% 8%" firstSpacing={16}>
            <Cell>氏名</Cell>
            <Cell>削除</Cell>
          </List>
          <List
            style={{ height: "48px" }}
            gridTemplateColumns="90% 8%"
            firstSpacing={16}
          >
            <Cell>{me?.name}</Cell>
          </List>
          {admins.map((admin) => (
            <div key={admin.userSub}>
              {admin.userSub !== userSub && (
                <List
                  style={{ height: "48px" }}
                  gridTemplateColumns="90% 8%"
                  firstSpacing={16}
                >
                  <Cell>{admin.name}</Cell>
                  <Cell>
                    <Icon
                      name="delete"
                      size="md"
                      onClick={() => {
                        setDeletingName({
                          name: admin.name,
                          userSub: admin.userSub,
                        });
                      }}
                    />
                  </Cell>
                </List>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const container = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  marginTop: "16px",
});

const box = css({
  padding: "16px",
  backgroundColor: color.gray50,
});

const buttonContainer = css({
  textAlign: "right",
});

const overlay = css({
  position: "fixed",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  top: 0,
  left: 0,
  zIndex: 20,
  backgroundColor: "#00000080",
});

const modalBody = {
  marginTop: "16px",
};

const modalSelectBox = {
  marginTop: "24px",
  width: "240px",
};
